"use client";

import {
  FloatingContext,
  useFloatingContext,
} from "@faire/slate/private/floating/FloatingContext";
import {
  useDismiss,
  useInteractions,
  useRole,
  useFloating,
  UseRoleProps,
} from "@floating-ui/react";
import React, { useCallback, useEffect } from "react";

import { ModalContext } from "./ModalContext";
import { ModalWidthVariant } from "./types";

type IProps = {
  open?: boolean;
  onClose?: () => void;
  width?: ModalWidthVariant;
  role?: UseRoleProps["role"];
  disableDismiss?: boolean;
  disableAnimations?: boolean;
};

export const ModalRoot: React.FC<React.PropsWithChildren<IProps>> = ({
  open,
  onClose,
  children,
  width,
  role,
  disableDismiss,
  disableAnimations,
}) => {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );

  return (
    <FloatingContext open={open} onOpenChange={handleOpenChange}>
      <ModalContext width={width} disableAnimations={disableAnimations}>
        <Root role={role} disableDismiss={disableDismiss}>
          {children}
        </Root>
      </ModalContext>
    </FloatingContext>
  );
};

const Root: React.FC<
  React.PropsWithChildren<{
    role?: UseRoleProps["role"];
    disableDismiss?: boolean;
  }>
> = ({ children, role: roleProp, disableDismiss }) => {
  const { floatingRootContext, setFloatingStyles, setInteractions } =
    useFloatingContext();

  const { floatingStyles } = useFloating({
    rootContext: floatingRootContext,
    transform: false,
  });

  const dismiss = useDismiss(floatingRootContext, {
    outsidePressEvent: "mousedown",
    enabled: !disableDismiss,
  });
  const role = useRole(floatingRootContext, { role: roleProp });

  const interactions = useInteractions([dismiss, role]);

  useEffect(() => {
    setInteractions(interactions);
  }, [interactions, setInteractions]);

  useEffect(() => {
    setFloatingStyles(floatingStyles);
  }, [floatingStyles, setFloatingStyles]);

  return children;
};
